export const WorkflowKind = {
  Foreground: 'Foreground',
  Background: 'Background',
  Subworkflow: 'Subworkflow',
} as const;
export type WorkflowKind = (typeof WorkflowKind)[keyof typeof WorkflowKind];

export const CreationMode = {
  Manual: 'Manual',
  Agent: 'Agent',
} as const;
export type CreationMode = (typeof CreationMode)[keyof typeof CreationMode];

export const StatusCategory = {
  ToDo: 'ToDo',
  InProgress: 'InProgress',
  Done: 'Done',
  Rejected: 'Rejected',
} as const;
export type StatusCategory =
  (typeof StatusCategory)[keyof typeof StatusCategory];

/**
 * The fixed palette a status may be painted with. Stored as this stable
 * English key — the Ukrainian name is display only, see
 * `pages/WorkflowBuilder/shared/status/statusColors.ts`.
 */
export const StatusColor = {
  Purple: 'Purple',
  Blue: 'Blue',
  Yellow: 'Yellow',
  Teal: 'Teal',
} as const;
export type StatusColor = (typeof StatusColor)[keyof typeof StatusColor];

export const WorkflowVersionState = {
  Draft: 'Draft',
  Published: 'Published',
  Retired: 'Retired',
} as const;
export type WorkflowVersionState =
  (typeof WorkflowVersionState)[keyof typeof WorkflowVersionState];

export const WorkflowVersionOrigin = {
  Human: 'Human',
  Agent: 'Agent',
} as const;
export type WorkflowVersionOrigin =
  (typeof WorkflowVersionOrigin)[keyof typeof WorkflowVersionOrigin];

/**
 * Statuses and transitions are identified by `key`: the `id` fields change on
 * every structure save, so nothing may be keyed off them.
 */
export interface WorkflowStatus {
  id: string;
  key: string;
  name: string;
  color: string;
  order: number;
  category: StatusCategory;
  isInitial: boolean;
  isFinal: boolean;
  /** Filled in by the user before an entity enters this status. */
  stepForm?: StepForm | null;
}

export interface WorkflowTransition {
  id: string;
  key: string;
  name: string;
  /** `null` — a global transition out of any status. */
  fromStatusKey: string | null;
  toStatusKey: string;
}

/**
 * One version as the workflow detail lists it. The structure is fetched per
 * version (`GET /workflows/{id}/versions/{versionId}`), so the list stays light
 * however many archived versions pile up.
 */
export interface WorkflowVersionSummary {
  id: string;
  number: number;
  state: WorkflowVersionState;
  origin: WorkflowVersionOrigin;
  createdAt: string;
  publishedAt: string | null;
  /** ISO timestamp of the version's last change. */
  updatedAt: string;
  updatedBy: WorkflowAuthor | null;
}

export interface WorkflowVersion extends WorkflowVersionSummary {
  statuses: WorkflowStatus[];
  transitions: WorkflowTransition[];
}

export interface WorkflowVersionArgs {
  workflowId: string;
  versionId: string;
}

/**
 * One row of the workflow list — a version, not a workflow: «Опубліковано»
 * lists each workflow's published version, «Чернетки» every draft and
 * archived one.
 */
export interface WorkflowVersionListItem {
  workflowId: string;
  versionId: string;
  number: number;
  state: WorkflowVersionState;
  name: string;
  kind: WorkflowKind;
  entityType: string | null;
  /** ISO timestamp of the last change to this version. */
  updatedAt: string;
  updatedBy: WorkflowAuthor | null;
  /** `Agent` — the change was made by the agent on `updatedBy`'s request. */
  updatedByOrigin: WorkflowVersionOrigin;
  publishedAt: string | null;
}

export interface WorkflowAuthor {
  id: string;
  name: string;
}

/**
 * At most one version is a `Draft` and at most one is `Published`; publishing
 * retires the previous published version and leaves the workflow without a
 * draft until one is created from it.
 */
export interface Workflow {
  id: string;
  name: string;
  description: string;
  kind: WorkflowKind;
  entityType: string | null;
  creationMode: CreationMode;
  /** Newest version first. */
  versions: WorkflowVersionSummary[];
}

/** Distinct statuses across every version of the workflow. */
export interface WorkflowStatusCatalogItem {
  key: string;
  name: string;
  color: string;
  category: StatusCategory;
}

/**
 * Distinct statuses across every workflow and version — the «Наявний статус»
 * catalog. Deduplicated by `key`: the first occurrence supplies `name`,
 * `color` and `category`, while `workflowNames` accumulates every workflow the
 * key appears in, so the seeded defaults stay distinguishable.
 */
export interface GlobalStatusCatalogItem extends WorkflowStatusCatalogItem {
  workflowNames: string[];
}

export interface CreateWorkflowPayload {
  name: string;
  description: string;
  kind: WorkflowKind;
  /** Required for `Foreground`. */
  entityType?: string;
  creationMode: CreationMode;
}

export interface CreateWorkflowResponse {
  id: string;
  /** The draft v1 the workflow is created with. */
  versionId: string;
}

export interface UpdateWorkflowPayload {
  id: string;
  name: string;
  description: string;
}

export interface DraftStatus {
  key: string;
  name: string;
  color: string;
  category: StatusCategory;
  isInitial: boolean;
  isFinal: boolean;
  stepForm?: StepForm | null;
}

/** What a step form field asks for and how the fill dialog renders it. */
export const StepFormFieldType = {
  Text: 'Text',
  Number: 'Number',
  Textarea: 'Textarea',
  Date: 'Date',
  /** One choice from the options typed into the builder. */
  Select: 'Select',
  /** One item of a backend dictionary («Вибір з довідника»). */
  PredefinedList: 'PredefinedList',
  Checkbox: 'Checkbox',
  CheckboxList: 'CheckboxList',
  RadioList: 'RadioList',
  File: 'File',
  YesNo: 'YesNo',
} as const;
export type StepFormFieldType =
  (typeof StepFormFieldType)[keyof typeof StepFormFieldType];

export interface StepFormFieldOption {
  label: string;
  value: string;
}

export interface StepFormField {
  id: string;
  /** Unique within the form; the key the filled value is sent under. */
  key: string;
  label: string;
  type: StepFormFieldType;
  /** The status change cannot run while the field is empty. */
  isRequired: boolean;
  /** A role from the approvers catalog; shown in the builder only. */
  filledByRoleId: string | null;
  /** Roles from the approvers catalog; empty — «Усі учасники». */
  visibleToRoleIds: string[];
  /** `Select`, `CheckboxList` and `RadioList` only. */
  options?: StepFormFieldOption[];
  /** `PredefinedList` only. */
  dictionaryId?: string | null;
}

/**
 * The fields a user fills in before an entity enters the status. Part of the
 * draft structure, so it is saved with «Оновити воркфлоу».
 */
export interface StepForm {
  fields: StepFormField[];
}

/** A dictionary a `PredefinedList` field picks its items from. */
export interface StepFormDictionary {
  id: string;
  name: string;
  items: StepFormFieldOption[];
}

/** A file is sent as its metadata only — nothing is uploaded yet. */
export interface StepFormFileValue {
  name: string;
  size: number;
}

export type StepFormValue =
  | string
  | number
  | boolean
  | string[]
  | StepFormFileValue
  | null;

/** Filled values keyed by `StepFormField.key`. */
export type StepFormValues = Record<string, StepFormValue>;

export interface EntityStepFormValuesArgs {
  workflowId: string;
  entityId: string;
  statusKey: string;
}

export interface DraftTransition {
  key: string;
  name: string;
  fromStatusKey: string | null;
  toStatusKey: string;
}

/** Whole rules per transition `key`, edited along with the structure. */
export type TransitionRuleEdits = Record<string, TransitionRules>;

/**
 * Replaces the whole draft structure — send the full status and transition
 * lists, not a delta. Allowed for `Foreground` workflows only.
 */
export interface SaveDraftStructurePayload {
  workflowId: string;
  statuses: DraftStatus[];
  transitions: DraftTransition[];
  /** Rules edited along with the structure, by transition `key`. */
  transitionRules?: TransitionRuleEdits;
}

/** What an approver stands for: everyone with a role, a department or one person. */
export const ApproverType = {
  Role: 'Role',
  Department: 'Department',
  User: 'User',
} as const;
export type ApproverType = (typeof ApproverType)[keyof typeof ApproverType];

export interface Approver {
  type: ApproverType;
  id: string;
  name: string;
  /** A user's position, e.g. «Керівник СБ»; roles and departments have none. */
  position?: string | null;
}

/** The catalog the «Додати погоджувача» picker offers, one list per type. */
export interface ApproverOptions {
  roles: Approver[];
  departments: Approver[];
  users: Approver[];
}

export const ApprovalMode = {
  /** The transition waits for every approver. */
  All: 'All',
  /** The first approval is enough. */
  Any: 'Any',
} as const;
export type ApprovalMode = (typeof ApprovalMode)[keyof typeof ApprovalMode];

export interface ApprovalRule {
  approvers: Approver[];
  mode: ApprovalMode;
  /**
   * Once the approvals are collected, the last approver decides separately
   * whether the transition runs or is rejected — the card does not move on its
   * own.
   */
  finalCheckByLastApprover: boolean;
}

/** Where a rejected case goes. */
export const RejectionMode = {
  /** Back to the transition's source status, for rework. */
  ReturnToStatus: 'ReturnToStatus',
  /** The case is closed and cannot re-enter the process. */
  ExitProcess: 'ExitProcess',
} as const;
export type RejectionMode = (typeof RejectionMode)[keyof typeof RejectionMode];

/**
 * Only the mode is stored — the return target is the transition's current
 * `fromStatusKey`, so editing the path never leaves a stale target behind.
 */
export interface RejectionRule {
  mode: RejectionMode;
}

/** What happens once a transition's SLA is breached. */
export const SlaEscalation = {
  NotifyManager: 'NotifyManager',
  NotifyAssignee: 'NotifyAssignee',
  Reassign: 'Reassign',
} as const;
export type SlaEscalation = (typeof SlaEscalation)[keyof typeof SlaEscalation];

/** What an action after the transition does. */
export const TransitionActionKind = {
  /** Notifies users, roles and departments. */
  Notification: 'Notification',
  /** Publishes a domain event other services listen to. */
  PublishEvent: 'PublishEvent',
  /** Runs the AI agent with the author's prompt. */
  RunAgent: 'RunAgent',
  /** Starts the transition's deadline: a warning, the breach, the escalation. */
  Sla: 'Sla',
} as const;
export type TransitionActionKind =
  (typeof TransitionActionKind)[keyof typeof TransitionActionKind];

export interface NotificationAction {
  /** Generated on the client when the author adds the action. */
  id: string;
  kind: typeof TransitionActionKind.Notification;
  recipients: Approver[];
}

export interface PublishEventAction {
  id: string;
  kind: typeof TransitionActionKind.PublishEvent;
  /** E.g. «ContractCase.SecurityChecked». */
  eventName: string;
}

export interface RunAgentAction {
  id: string;
  kind: typeof TransitionActionKind.RunAgent;
  prompt: string;
}

/** An employee of the organization the SLA names. */
export interface SlaEmployee {
  id: string;
  /** «Прізвище Імʼя По батькові», as picked. */
  name: string;
}

/**
 * The transition's deadline, counted from the moment the case entered its
 * source status: a warning first, then the breach that triggers escalation.
 */
export interface SlaAction {
  id: string;
  kind: typeof TransitionActionKind.Sla;
  warningAfterMinutes: number;
  breachAfterMinutes: number;
  escalation: SlaEscalation;
  employee: SlaEmployee;
}

/**
 * The process-wide SLA of a version («Налаштування › SLA процесу»), counted
 * from the case's creation to its final status: the transition SLA's fields.
 */
export type ProcessSla = Omit<SlaAction, 'id' | 'kind'>;

export interface ProcessSlaSettings {
  /** `null` while switched off. */
  sla: ProcessSla | null;
}

/** Replaces the version's process SLA. */
export interface SaveProcessSlaSettingsPayload
  extends WorkflowVersionArgs, ProcessSlaSettings {}

/** One action the author configured on the transition. */
export type TransitionAction =
  | NotificationAction
  | PublishEventAction
  | RunAgentAction
  | SlaAction;

/**
 * The actions run in this order after the final check. The status change is
 * not stored — every transition runs it, always last.
 */
export interface ActionsRule {
  actions: TransitionAction[];
}

/** One check from the catalog the backend offers. */
export interface TransitionValidator {
  id: string;
  /** E.g. «Причина обовʼязкова при відхиленні». */
  name: string;
  description: string;
}

/**
 * The checks run during the transition; the first failure stops it. The
 * source status's step form check is not stored — every transition runs it.
 */
export interface ValidatorsRule {
  validators: TransitionValidator[];
}

/** One field from the catalog the backend offers, written into the card. */
export interface TransitionInputField {
  id: string;
  /** E.g. «Висновок погодження». */
  name: string;
  /** Its type and the card property it fills. */
  description: string;
  /** The transition cannot run while the field is empty. */
  isRequired: boolean;
}

/** What has to be filled in while running the transition. */
export interface InputRule {
  fields: TransitionInputField[];
}

/** One condition from the catalog the backend offers. */
export interface TransitionRestriction {
  id: string;
  /** E.g. «Лише погоджувачі цього переходу». */
  name: string;
  description: string;
}

export const RestrictionMode = {
  /** Only the approvers of the transition's approval rule. */
  ApproversOnly: 'approvers-only',
  /** Only the chosen roles, departments and employees. */
  Audience: 'audience',
} as const;
export type RestrictionMode =
  (typeof RestrictionMode)[keyof typeof RestrictionMode];

/**
 * Who sees and may run the transition. Everyone else does not see it in the
 * status select and cannot move a card through it.
 */
export type RestrictionsRule =
  | { mode: typeof RestrictionMode.ApproversOnly }
  | {
      mode: typeof RestrictionMode.Audience;
      /** A user passes by matching any entry: themselves, a role or a department. */
      audience: Approver[];
    };

/**
 * A transition's rules. They belong to a version — the draft's are edited, a
 * published or archived version's are frozen — but live outside the draft
 * structure, saved on their own and keyed by the transition's stable `key`.
 */
export interface TransitionRules {
  approval: ApprovalRule | null;
  rejection: RejectionRule | null;
  actions: ActionsRule | null;
  validators: ValidatorsRule | null;
  restrictions: RestrictionsRule | null;
  input: InputRule | null;
}

export interface TransitionRulesArgs extends WorkflowVersionArgs {
  transitionKey: string;
}

export const WorkflowErrorCode = {
  HasNoDraft: 'Workflow.HasNoDraft',
  /** Publishing would leave board cards in a status the draft removed. */
  CardsInRemovedStatus: 'Workflow.CardsInRemovedStatus',
  NotFound: 'General.NotFound',
  StructureNotAllowed: 'Structure.NotAllowed',
  StatusInitialMissing: 'Status.InitialMissing',
  StatusInitialMultiple: 'Status.InitialMultiple',
  StatusFinalMissing: 'Status.FinalMissing',
  StatusKeyDuplicate: 'Status.KeyDuplicate',
  StatusNoOutgoing: 'Status.NoOutgoing',
  StatusUnreachable: 'Status.Unreachable',
  TransitionKeyDuplicate: 'Transition.KeyDuplicate',
  TransitionStatusNotFound: 'Transition.StatusNotFound',
  /** The card took a transition whose approval is not complete yet. */
  TransitionApprovalRequired: 'Transition.ApprovalRequired',
  /** The card already waits for approval of another transition. */
  TransitionRequestPending: 'Transition.RequestPending',
  /** Checked on the client: a board column must hold at least one status. */
  BoardColumnWithoutStatuses: 'Board.ColumnWithoutStatuses',
} as const;
export type WorkflowErrorCode =
  (typeof WorkflowErrorCode)[keyof typeof WorkflowErrorCode];

export interface WorkflowApiErrorItem {
  errorMessage: string;
  /** Request field the error belongs to, e.g. `name`, `statuses[0].name`. */
  propertyName?: string;
  errorCode: WorkflowErrorCode | string;
  /** A hint string for structure errors, `{ id }` for `General.NotFound`. */
  state?: string | Record<string, unknown>;
}

/**
 * Every failure — validation, "not found" — comes back as
 * HTTP 400 with this body; there is no 404.
 */
export interface WorkflowApiErrorResponse {
  status: number;
  title: string;
  traceId: string;
  errors: WorkflowApiErrorItem[];
}

/**
 * A board card. Cards belong to the workflow, not to a version — every
 * version's board shows the same ones. The workflow's `entityType` is
 * arbitrary, so the only field the board reads and writes is `statusKey`; it
 * matches a `WorkflowStatus.key` of some version, not necessarily the one on
 * screen.
 */
export interface WorkflowEntityItem {
  id: string;
  title: string;
  statusKey: string;
  createdAt: string;
  /** The transition the card waits to be approved for, if it waits. */
  pendingTransition?: PendingTransitionSummary | null;
}

/** What the board tile shows of an open transition request. */
export interface PendingTransitionSummary {
  toStatusKey: string;
  approvedCount: number;
  requiredCount: number;
}

export interface ChangeWorkflowEntityStatusPayload {
  workflowId: string;
  entityId: string;
  toStatusKey: string;
  /** The target status's step form, when it has one. */
  formValues?: StepFormValues;
}

export interface CreateWorkflowEntityPayload {
  workflowId: string;
  title: string;
  /** The status the card lands in — the board resolves it from the column. */
  statusKey: string;
}

export interface CreateWorkflowEntityResponse {
  id: string;
}

export interface UpdateWorkflowEntityPayload {
  workflowId: string;
  entityId: string;
  title: string;
  statusKey: string;
  /** The target status's step form, when the status changes into one. */
  formValues?: StepFormValues;
  /**
   * Edits to the step forms of statuses the card has already been in, keyed by
   * status; only the forms that changed. Merged into what is stored.
   */
  stepFormValues?: Record<string, StepFormValues>;
}

/**
 * What a history row records. `Assigned`/`Reassigned` exist because the real
 * contract will emit them — a card has no assignee yet, so nothing produces
 * them today.
 */
export const WorkflowEntityEventType = {
  Created: 'Created',
  Renamed: 'Renamed',
  StatusChanged: 'StatusChanged',
  Assigned: 'Assigned',
  Reassigned: 'Reassigned',
  /** A step form filled earlier was edited in the card. */
  StepFormUpdated: 'StepFormUpdated',
  /** A move into `toStatusKey` waits for approval. */
  TransitionRequested: 'TransitionRequested',
  /** An approver approved the card's transition into `toStatusKey`. */
  Approved: 'Approved',
  /** The request to move into `toStatusKey` was withdrawn. */
  TransitionCancelled: 'TransitionCancelled',
} as const;
export type WorkflowEntityEventType =
  (typeof WorkflowEntityEventType)[keyof typeof WorkflowEntityEventType];

/** One change of a board card, oldest first. */
export interface WorkflowEntityHistoryItem {
  id: string;
  type: WorkflowEntityEventType;
  occurredAt: string;
  fromStatusKey?: string | null;
  toStatusKey?: string | null;
  fromTitle?: string | null;
  toTitle?: string | null;
  toAssigneeName?: string | null;
  /** `StepFormUpdated`: the status whose step form was edited. */
  stepFormStatusKey?: string | null;
  /** `Approved`: the transition approved and the approver it counts for. */
  transitionKey?: string | null;
  approverName?: string | null;
  changedByDisplayName?: string | null;
}

export const EntityApproverStatus = {
  Pending: 'Pending',
  Approved: 'Approved',
} as const;
export type EntityApproverStatus =
  (typeof EntityApproverStatus)[keyof typeof EntityApproverStatus];

/** One approver of a card's transition and their decision so far. */
export interface EntityApproverState {
  approver: Approver;
  status: EntityApproverStatus;
  decidedAt?: string | null;
  decidedByDisplayName?: string | null;
}

/**
 * A card's open request to take a transition whose rule asks for approval. The
 * card stays in its status until the request is approved and the move done;
 * one request per card, and a status change closes it.
 */
export interface EntityTransitionRequest {
  id: string;
  transitionKey: string;
  toStatusKey: string;
  createdAt: string;
  createdByDisplayName?: string | null;
  mode: ApprovalMode;
  finalCheckByLastApprover: boolean;
  /** `All` — every approver, `Any` — one. */
  requiredCount: number;
  approvedCount: number;
  approvers: EntityApproverState[];
  /** `approvedCount >= requiredCount` — the card may take the transition. */
  isApproved: boolean;
  /** Approved, and the rule leaves the move to the last approver's check. */
  awaitingFinalCheck: boolean;
  /**
   * Approved, but nothing moved the card: the final check or the target
   * status's step form needs someone. Without either the last approval moves
   * the card itself.
   */
  awaitingMove: boolean;
  /** The current user may approve a pending approver's slot. */
  canApprove: boolean;
}

export interface EntityTransitionRequestArgs {
  workflowId: string;
  entityId: string;
}

export interface CreateEntityTransitionRequestPayload extends EntityTransitionRequestArgs {
  toStatusKey: string;
}

export interface ApproveEntityTransitionPayload extends EntityTransitionRequestArgs {
  approverId: string;
}

/**
 * One column of a persisted board layout. Structurally the board's own
 * `BoardColumn`, kept separate because this one is a wire type — the board is
 * free to carry view-only fields the backend never stores.
 */
export interface BoardLayoutColumn {
  id: string;
  title: string;
  color?: string;
  /**
   * Statuses the column owns, in order. `statusKeys[0]` is the primary one: a
   * card dropped on the column moves into it. A status listed in no column is
   * legal — its cards simply do not appear on the board.
   */
  statusKeys: string[];
}

/** `columns: null` — the user never customised the board; use the default. */
export interface BoardLayout {
  columns: BoardLayoutColumn[] | null;
}

/** Replaces the whole layout. `columns: null` resets it to the default. */
export interface SaveBoardLayoutPayload {
  workflowId: string;
  columns: BoardLayoutColumn[] | null;
}

/** What starts a global automation rule. */
export const AutomationTriggerKind = {
  /** Any transition, or one picked by key, has run. */
  TransitionExecuted: 'TransitionExecuted',
  /** The card has entered the status, whatever transition brought it. */
  StatusEntered: 'StatusEntered',
} as const;
export type AutomationTriggerKind =
  (typeof AutomationTriggerKind)[keyof typeof AutomationTriggerKind];

export interface TransitionExecutedTrigger {
  kind: typeof AutomationTriggerKind.TransitionExecuted;
  /** `null` — any transition of the version. */
  transitionKey: string | null;
}

export interface StatusEnteredTrigger {
  kind: typeof AutomationTriggerKind.StatusEntered;
  statusKey: string;
}

export type AutomationTrigger =
  | TransitionExecutedTrigger
  | StatusEnteredTrigger;

/** A check on the triggering move; every condition of a rule has to hold. */
export const AutomationConditionKind = {
  /** The card came from one of the statuses. */
  PreviousStatusIn: 'PreviousStatusIn',
  /** The card came from none of the statuses. */
  PreviousStatusNotIn: 'PreviousStatusNotIn',
  /** The move was one of the transitions. */
  TransitionIs: 'TransitionIs',
  /** A field of a step form the card filled earlier holds the value. */
  StepFormValueEquals: 'StepFormValueEquals',
} as const;
export type AutomationConditionKind =
  (typeof AutomationConditionKind)[keyof typeof AutomationConditionKind];

/** The conditions that pick from a list of statuses or transitions. */
export type AutomationListConditionKind = Exclude<
  AutomationConditionKind,
  typeof AutomationConditionKind.StepFormValueEquals
>;

export interface AutomationListCondition {
  /** Generated on the client when the author adds the condition. */
  id: string;
  kind: AutomationListConditionKind;
  /** Status keys, or transition keys for `TransitionIs`. */
  keys: string[];
}

export interface StepFormValueCondition {
  id: string;
  kind: typeof AutomationConditionKind.StepFormValueEquals;
  /** The status whose step form holds the field. */
  statusKey: string;
  /** `StepFormField.key`. */
  fieldKey: string;
  /**
   * Compared as text: an option's `value`, `yes`/`no` for `YesNo`,
   * `true`/`false` for `Checkbox`; a `CheckboxList` holds it among the picked.
   */
  value: string;
}

export type AutomationCondition =
  | AutomationListCondition
  | StepFormValueCondition;

/** What a global automation rule does once it fires. */
export const AutomationActionKind = {
  /** Moves the card on to a status, bypassing the transition graph. */
  MoveToStatus: 'MoveToStatus',
  SendEmail: 'SendEmail',
  Notify: 'Notify',
  UpdateField: 'UpdateField',
} as const;
export type AutomationActionKind =
  (typeof AutomationActionKind)[keyof typeof AutomationActionKind];

/** Whom an email or a notification goes to, resolved from the card. */
export const AutomationRecipient = {
  CaseManager: 'CaseManager',
  Assignee: 'Assignee',
} as const;
export type AutomationRecipient =
  (typeof AutomationRecipient)[keyof typeof AutomationRecipient];

export const AutomationNotifyChannel = {
  Teams: 'Teams',
  InApp: 'InApp',
} as const;
export type AutomationNotifyChannel =
  (typeof AutomationNotifyChannel)[keyof typeof AutomationNotifyChannel];

/** The card fields an `UpdateField` action may change. */
export const AutomationField = {
  /** Cleared — the card is left without an assignee. */
  Assignee: 'Assignee',
} as const;
export type AutomationField =
  (typeof AutomationField)[keyof typeof AutomationField];

/** What happens once the action has run. */
export const AutomationAfterExecution = {
  None: 'None',
  /** The rules further down the list are skipped for this move. */
  StopOtherRules: 'StopOtherRules',
} as const;
export type AutomationAfterExecution =
  (typeof AutomationAfterExecution)[keyof typeof AutomationAfterExecution];

interface AutomationActionBase {
  /** Generated on the client when the author adds the action. */
  id: string;
  afterExecution: AutomationAfterExecution;
}

export interface MoveToStatusAction extends AutomationActionBase {
  kind: typeof AutomationActionKind.MoveToStatus;
  statusKey: string;
}

export interface SendEmailAction extends AutomationActionBase {
  kind: typeof AutomationActionKind.SendEmail;
  recipient: AutomationRecipient;
  templateId: string;
}

export interface NotifyAction extends AutomationActionBase {
  kind: typeof AutomationActionKind.Notify;
  channel: AutomationNotifyChannel;
  recipient: AutomationRecipient;
}

export interface UpdateFieldAction extends AutomationActionBase {
  kind: typeof AutomationActionKind.UpdateField;
  field: AutomationField;
}

export type AutomationAction =
  | MoveToStatusAction
  | SendEmailAction
  | NotifyAction
  | UpdateFieldAction;

/**
 * A global automation rule of a version: «Коли» a trigger fires, «Якщо» every
 * condition holds, «Тоді» the actions run in order. Rules run in list order.
 */
export interface AutomationRule {
  /** Generated on the client when the author adds the rule. */
  id: string;
  isEnabled: boolean;
  /** Written by a person in the UI or by the agent. */
  origin: WorkflowVersionOrigin;
  createdBy: WorkflowAuthor | null;
  createdAt: string;
  /** Optional validity period, ISO dates. */
  activeFrom: string | null;
  activeTo: string | null;
  trigger: AutomationTrigger;
  conditions: AutomationCondition[];
  actions: AutomationAction[];
}

/** One email template an action may send. */
export interface AutomationEmailTemplate {
  id: string;
  name: string;
}

/** Replaces the version's rules wholesale, in run order. */
export interface SaveAutomationRulesPayload extends WorkflowVersionArgs {
  rules: AutomationRule[];
}

/** A draft copied from the published version, for further changes. */
export interface CreateWorkflowDraftPayload {
  workflowId: string;
  /** The published version it is copied from. */
  fromVersionId: string;
}

export interface CreateWorkflowDraftResponse {
  versionId: string;
  number: number;
}

export interface PublishWorkflowResponse {
  versionId: string;
  number: number;
}

/** One problem the server found in the draft. */
export interface WorkflowDraftError {
  /** E.g. `transitions[0].rules.approval.requiredCount`. */
  path: string;
  code: string;
  message: string;
  hint: string;
  /** `false` — the draft saves, only publishing is blocked. */
  blocksDraftSave: boolean;
}

export interface WorkflowDraftValidation {
  canSaveDraft: boolean;
  canPublish: boolean;
  errors: WorkflowDraftError[];
}
